import { Box, Button, Flex, Heading, Input, InputGroup, SimpleGrid, Text } from '@chakra-ui/react';
import { UseFormReturn, useController } from 'react-hook-form';
import { useDebouncedFieldRegistry } from '@/utils/filterUtils/useDebouncedFieldRegistry';
import { ChakraFeatherIcon } from 'src/components/ChakraFeatherIcon';
import { SelectField } from 'src/components/SelectField';
import { Filters } from '../../types';
import { SubscriptionPlan, FinancialTransactionType } from '@/model';
import { DateRangeFilterFields } from '@/components/DateRangeFilterFields';
import { DateRangeChangeHandler } from '@/components/DateRangeFilterFields/types';
import { useTranslation } from 'react-i18next';

export interface FinancialFiltersBoxProps {
  filtersForm: UseFormReturn<Filters>;
  subscriptionPlans: SubscriptionPlan[];
}

export const FinancialFiltersBox = ({ filtersForm, subscriptionPlans }: FinancialFiltersBoxProps) => {
  const { t } = useTranslation();
  const { register, reset, control } = filtersForm;

  const dateValueControl = useController({
    name: 'dateValue',
    control,
  });
  const startDateControl = useController({
    name: 'start_date',
    control,
  });
  const endDateControl = useController({
    name: 'end_date',
    control,
  });

  const handleRangeChange: DateRangeChangeHandler = (startDate, endDate) => {
    startDateControl.field.onChange(startDate);
    endDateControl.field.onChange(endDate);
  };

  return (
    <Box>
      <Flex align='center' gap='pico' mb='deka'>
        <ChakraFeatherIcon color='text.main' name='filter' />
        <Heading variant='sectionTitle'>{t('pages.financial.components.financial_filters_box.financial_filters_title')}</Heading>
      </Flex>

      <Flex direction={{ base: 'column' }} gap='small' rowGap='micro'>
        <SimpleGrid gap='deka' alignItems='flex-end' columns={{ base: 1, sm: 2, md: 4 }} rowGap='xxsmall' flexGrow={1}>
          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>{t('pages.financial.components.financial_filters_box.financial_filters_subscriber_label')}</Text>
            <Input variant='flushed' {...useDebouncedFieldRegistry(() => register('name'))} />
          </InputGroup>

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>{t('pages.financial.components.financial_filters_box.financial_filters_transaction_type_label')}</Text>
            <SelectField {...register('transaction_type')}>
              <option value=''>{t('pages.financial.components.financial_filters_box.financial_filters_all_option')}</option>
              <option value={FinancialTransactionType.MONTHLY_PAYMENT}>{t('pages.financial.components.financial_filters_box.financial_filters_monthly_payment')}</option>
              <option value={FinancialTransactionType.EXPERIENCE}>{t('pages.financial.components.financial_filters_box.financial_filters_experience')}</option>
            </SelectField>
          </InputGroup>

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>{t('pages.financial.components.financial_filters_box.financial_filters_plan_label')}</Text>
            <SelectField {...register('subscription_plan')}>
              <option value=''>{t('pages.financial.components.financial_filters_box.financial_filters_all_option')}</option>
              {subscriptionPlans?.map(subscriptionPlan => (
                <option key={subscriptionPlan.id} value={subscriptionPlan.id}>
                  {subscriptionPlan.name}
                </option>
              ))}
            </SelectField>
          </InputGroup>
        </SimpleGrid>

        <SimpleGrid gap='deka' alignItems='flex-end' columns={{ base: 1, sm: 2, md: 4 }} rowGap='xxsmall' flexGrow={1}>
          <DateRangeFilterFields
            onRangeChange={handleRangeChange}
            startDate={startDateControl.field.value}
            endDate={endDateControl.field.value}
            dateValue={dateValueControl.field.value}
            onDateValueChange={dateValueControl.field.onChange}
            inputName='partner-actions-date'
          />
        </SimpleGrid>

        <Button alignSelf={{ lg: 'end' }} colorScheme='text' variant='outline' mt='10px' onClick={() => reset()}>
          {t('pages.financial.components.financial_filters_box.financial_filters_clear_filters')}
        </Button>
      </Flex>
    </Box>
  );
};